'use client'

import styles from './SiteMapSayt.module.scss'

import React, { useEffect, useState } from 'react'

import Carousel from 'Clutch/Organisms/Carousel'

import { Default, Selected } from 'Clutch/Molecules/Button/Variants/OptionButtons'

import Form from 'Atoms/Form'
import Skeleton from 'Clutch/Atoms/Skeleton'

const SiteMapSayt = ({ facetName, links, ChildComponent, childProps, quickSearchButtons = [], alphaFilters = [] }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [waiting, setWaiting] = useState(false)
  const [searchResults, setSearchResults] = useState(links || [])
  const [quickSearchValue, setQuickSearchValue] = useState(false)
  const [alphaFilterValue, setAlphaFilterValue] = useState(false)

  const displayTitle =
    facetName.endsWith('s') || facetName.endsWith('y') || facetName.endsWith('h') || facetName.endsWith('x')
      ? facetName
      : `${facetName}s`

  if (typeof window != 'undefined') {
    window.addEventListener('popstate', (event) => {
      if (document.getElementById(`${facetName}_search_bar`))
        document.getElementById(`${facetName}_search_bar`).value = ''
      setSearchTerm('')
    })
  }

  useEffect(() => {
    doSearch(searchTerm)
  }, [facetName])

  const doSearch = async (term) => {
    setWaiting(true)
    const isNested = links.some((x) => x.subsections)
    term = term.trim()
    setSearchTerm(term)
    if (!term.length) {
      if (alphaFilterValue) {
        setSearchResults(ApplyAlphaFilter(links))
      }
      EndWaiting()
      return
    }

    if (isNested) {
      DoNestedSearch(term)
    } else {
      DoSimpleSearch(term)
    }
  }

  const DoSimpleSearch = async (term) => {
    var list = ApplyAlphaFilter(links)
    list = list.filter((x) => checkRegex(x.displayValue, term))
    list = list?.sort(
      (a, b) =>
        b?.displayValue?.toLowerCase().startsWith(term) - a?.displayValue?.toLowerCase().startsWith(term.toLowerCase()),
    )
    setSearchResults(list ?? [])
    EndWaiting()
  }

  const EndWaiting = () => {
    //timeout is important to trigger a re-render for suspense state, otherwise there is horrific input delay on larger sitemap pages
    setTimeout(() => setWaiting(false), 1)
  }

  const ApplyAlphaFilter = (list) => {
    if (alphaFilterValue) {
      if (alphaFilterValue == '#') {
        list = list.filter((x) => !isNaN(parseFloat(x.displayValue[0])))
      } else {
        list = list.filter((x) => x.displayValue[0].toLowerCase() === alphaFilterValue.toLowerCase())
      }
    }
    return list
  }

  const DoNestedSearch = async (term) => {
    const results = []
    const clonedLinks = JSON.parse(JSON.stringify(ApplyAlphaFilter(links)))

    for (let i = 0, ilen = clonedLinks.length; i < ilen; i++) {
      if (checkRegex(clonedLinks[i].displayValue, term)) {
        results.push(clonedLinks[i])
        continue
      }
      const filteredSubLinks = clonedLinks[i].subsections.filter(
        (x) => checkRegex(x.displayValue, term) || checkRegex(x.fullText, term),
      )
      if (filteredSubLinks.length) {
        results.push(clonedLinks[i])
        results[results.length - 1].subsections = filteredSubLinks
      }
    }
    setSearchResults(results ?? [])
    EndWaiting()
  }

  const checkRegex = (displayValue, searchTerm) => {
    if (!displayValue) {
      return false
    }
    var regex = new RegExp(`(${searchTerm})+`, 'i')
    var match = displayValue.match(regex)
    return match?.[0]
  }

  const handleChange = (e) => {
    e.preventDefault()
    const term = e.nativeEvent.srcElement.value
    doSearch(term)
  }

  const DoQuickSearch = async (value) => {
    setWaiting(true)
    if (quickSearchValue === value) {
      value = ''
    }
    setQuickSearchValue(value)
    doSearch(value)
  }

  const DoAlphaFilter = async (value) => {
    setWaiting(true)
    if (alphaFilterValue === value) {
      value = ''
    }
    setAlphaFilterValue(value)
  }
  useEffect(() => {
    doSearch(searchTerm)
  }, [alphaFilterValue])

  const searchBar = quickSearchValue ? null : (
    <div className={styles.sitemapSearchBar}>
      <Form.Textbox
        id={`${facetName}_search_bar`}
        fill
        placeholder={`Search ${displayTitle}`}
        data-testid={`${facetName}_search_bar_input`}
        onChange={handleChange}
      />
    </div>
  )

  if (waiting) {
    ChildComponent = SkeletonLoader
  }

  const alphaFilter = !alphaFilters.length ? null : (
    <>
      <div className={styles.quickSearchBlock}>
        <Carousel>
          {alphaFilters.map((x) => {
            return (
              <Carousel.Item key={`${x}`}>
                {alphaFilterValue == x ? (
                  <Selected onClick={() => DoAlphaFilter(x)} value={{ displayName: x }} brand={'primary'} />
                ) : (
                  <Default onClick={() => DoAlphaFilter(x)} value={{ displayName: x }} />
                )}
              </Carousel.Item>
            )
          })}
        </Carousel>
      </div>
    </>
  )

  const quickSearch = !quickSearchButtons.length ? null : (
    <>
      <div className={styles.quickSearchBlock}>
        <Carousel>
          {quickSearchButtons.map((x) => {
            return (
              <Carousel.Item key={`${x}`}>
                {quickSearchValue == x ? (
                  <Selected onClick={() => DoQuickSearch(x)} value={{ displayName: x }} brand={'primary'} />
                ) : (
                  <Default onClick={() => DoQuickSearch(x)} value={{ displayName: x }} />
                )}
              </Carousel.Item>
            )
          })}
        </Carousel>
      </div>
    </>
  )

  if (!searchTerm.length && !alphaFilterValue.length) {
    return (
      <>
        {quickSearch}
        {searchBar}
        {alphaFilter}
        <ChildComponent links={links} isFiltered={true} {...childProps} />
      </>
    )
  }

  return (
    <>
      {quickSearch}
      {searchBar}
      {alphaFilter}
      <ChildComponent links={searchResults} isFiltered={true} {...childProps} />
    </>
  )
}

const SkeletonLoader = () => {
  return (
    <>
      <Skeleton height={300} percentWidth width={100} />
      <Skeleton height={300} percentWidth width={100} />
      <Skeleton height={300} percentWidth width={100} />
    </>
  )
}

export default SiteMapSayt
